import * as changeCase from 'change-case';
import { useCommandState } from 'cmdk';
import * as R from 'ramda';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import AccountLogo from 'components/accounts/AccountLogo';
import { CommandGroup } from 'components/commandPalette/base/CommandGroup';
import Emoji from 'components/lib/ui/Emoji';
import Icon from 'components/lib/ui/Icon';
import Text from 'components/lib/ui/Text';

import useDebounce from 'common/lib/hooks/useDebounce';
import useQuery from 'common/lib/hooks/useQuery';
import { radius } from 'common/lib/theme/dynamic';
import { pluralize } from 'common/utils/Number';
import { capitalize, isEmoji } from 'common/utils/String';
import {
  useCommandRegistry,
  useGetCommandsByGroup,
  useRegisterContextualCommands,
} from 'lib/commands/context';
import type { MonarchEntityCommand } from 'lib/commands/types';
import { getRouteForResultType } from 'lib/globalSearch/routes';

import routes from 'constants/routes';

import { gql } from 'common/generated';
import { SearchResultType } from 'common/generated/graphql';

const ICON_SIZE_PX = 16;
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_MIN_LENGTH = 2;

type ItemIconProps = {
  icon: Maybe<string>;
  logoUrl: Maybe<string>;
  type: SearchResultType;
};

/**
 * A generic component that renders commands for a given entity type.
 */
export const DynamicEntityCommands = () => {
  const commands = useGetCommandsByGroup('entities');
  const search = useCommandState((state) => state.search);
  const isSearchEmpty = search.length === 0;

  const getEntityTypeTitle = useCallback((entityType: string) => {
    if (entityType === SearchResultType.REPORT_CONFIGURATION) {
      return 'Saved Reports';
    }

    return capitalize(pluralize(entityType));
  }, []);

  const commandsByEntityType = useMemo(
    () =>
      // Sort the commands by score and then group them by entity type
      R.sort((a, b) => (b.score ?? 0) - (a.score ?? 0), commands).reduce(
        (acc, command) => {
          if (command.type) {
            acc[command.type] = [...(acc[command.type] || []), command];
          }
          return acc;
        },
        {} as Record<string, typeof commands>,
      ),
    [commands],
  );

  return (
    // We need to wrap this in a Fragment because React thinks the
    // `map` returns more than one child (Element[] instead of Element).
    <>
      {Object.entries(commandsByEntityType).map(([entityType, commands]) => (
        <CommandGroup.Root
          key={entityType}
          group="entities"
          heading={getEntityTypeTitle(entityType)}
          rightContent={
            isSearchEmpty && (
              <Text weight="book" size="xsmall">
                most recent searches
              </Text>
            )
          }
        >
          {commands.map((command) => (
            <CommandGroup.Item key={command.id} {...command} />
          ))}
        </CommandGroup.Root>
      ))}
    </>
  );
};

/**
 * A hook that registers commands for entities based on a search term.
 *
 * Check the other comments for more context.
 */
export const useRegisterEntityCommands = (search: string) => {
  const { registerCommand } = useCommandRegistry();
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const history = useHistory();

  const { data, isNetworkRequestInFlight } = useQuery(GET_COMMAND_PALETTE_ENTITIES, {
    variables: { query: debouncedSearch || '' },
    fetchPolicy: 'cache-first',
    skip: debouncedSearch.length < SEARCH_MIN_LENGTH,
  });

  const { data: reportConfigurationsData } = useQuery(GET_REPORT_CONFIGURATIONS, {
    fetchPolicy: 'cache-first',
  });

  const { semanticSearch } = data || {};
  const { results: semanticSearchResults } = semanticSearch || {};
  const { reportConfigurations } = reportConfigurationsData || {};

  const getScoreForEntityCommand = useCallback(
    (type: SearchResultType) => {
      if (
        type === SearchResultType.REPORT_CONFIGURATION &&
        history.location.pathname.includes(routes.reports.path)
      ) {
        return 1;
      }

      return 0;
    },
    [history.location.pathname],
  );

  useEffect(() => {
    if (semanticSearchResults) {
      semanticSearchResults.forEach((result) =>
        registerCommand({
          id: getCommandId(result.type, result.id),
          label: result.name,
          icon: <ItemIcon icon={result.icon} logoUrl={result.logoUrl} type={result.type} />,
          group: 'entities',
          // This is what declares what entity type the command is for.
          // The values come from the backend (merchant/category/account/etc).
          type: result.type,
          keywords: [result.name],
          action: () => history.push(getRouteForResultType(result.type, result.id)),
          analyticsMetadata: {
            analyticsId: getCommandId(result.type),
            entityType: result.type,
            entityId: result.id,
          },
          score: getScoreForEntityCommand(result.type),
        }),
      );
    }
  }, [semanticSearchResults, registerCommand, history, getScoreForEntityCommand]);

  // Register all saved reports as commands so they're easily accessible
  const reportConfigurationsCommands = useMemo(
    () =>
      (reportConfigurations ?? []).map<MonarchEntityCommand>(({ id, displayName }) => ({
        id: getCommandId(SearchResultType.REPORT_CONFIGURATION, id),
        label: displayName,
        icon: <Icon name="bookmark" />,
        group: 'entities',
        type: SearchResultType.REPORT_CONFIGURATION,
        keywords: [displayName, 'saved', 'reports'],
        action: () =>
          history.push(getRouteForResultType(SearchResultType.REPORT_CONFIGURATION, id)),
        score: getScoreForEntityCommand(SearchResultType.REPORT_CONFIGURATION),
        analyticsMetadata: {
          analyticsId: getCommandId(SearchResultType.REPORT_CONFIGURATION),
          entityType: SearchResultType.REPORT_CONFIGURATION,
          entityId: id,
        },
      })),
    [reportConfigurations, getScoreForEntityCommand, history],
  );

  useRegisterContextualCommands(reportConfigurationsCommands);

  return { isLoading: isNetworkRequestInFlight };
};

const ItemIcon = React.memo(({ icon, logoUrl, type }: ItemIconProps) => {
  if (type === SearchResultType.GOAL && logoUrl) {
    return <GoalIcon src={logoUrl} />;
  } else if (type === SearchResultType.REPORT_CONFIGURATION) {
    return <Icon name="bookmark" />;
  } else if (icon) {
    return isEmoji(icon) ? <Emoji>{icon}</Emoji> : <Icon name={icon} />;
  }

  return <AccountLogo logoUrl={logoUrl} size={ICON_SIZE_PX} />;
});

const GoalIcon = styled.img`
  width: ${ICON_SIZE_PX}px;
  height: ${ICON_SIZE_PX}px;
  border-radius: ${radius.xxsmall};
  object-fit: cover;
  object-position: center;
  overflow: hidden;
`;

const GET_COMMAND_PALETTE_ENTITIES = gql(/* GraphQL */ `
  query Web_GetCommandPaletteEntities($query: String!) {
    semanticSearch(query: $query) {
      results {
        id
        type
        name
        icon
        logoUrl
      }
    }
  }
`);

const GET_REPORT_CONFIGURATIONS = gql(/* GraphQL */ `
  query Web_GetReportConfigurationsForCommandPalette {
    reportConfigurations {
      id
      displayName
    }
  }
`);

const getCommandId = (type: SearchResultType, id?: string) =>
  id
    ? `navigate-to-${changeCase.paramCase(type)}:${id}`
    : `navigate-to-${changeCase.paramCase(type)}`;
